import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { Payment } from "@/model/payment";
import { Lottie } from "lottie-react";
import successAnimation from "../../lottie-animations/success.json";
import { isMobileOnly } from "react-device-detect";
import { Download } from "lucide-react";
import { useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { ReceiptPDF } from "./ReceiptPDF";

interface ReceiptProps {
  payment: Payment;
}

const formatCurrency = (amount: number): string => {
  return (
    new Intl.NumberFormat("de-DE", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount) + " kr."
  );
};

const formatDate = (
  timestamp: string | number | { $numberLong?: string } | undefined,
): string => {
  if (!timestamp) return "";

  let date: Date;

  if (typeof timestamp === "string") {
    // Check if it's a number string
    const num = parseInt(timestamp, 10);
    if (!isNaN(num)) {
      date = new Date(num * 1000); // Convert Unix timestamp to milliseconds
    } else {
      date = new Date(timestamp);
    }
  } else if (typeof timestamp === "number") {
    date = new Date(timestamp * 1000); // Convert Unix timestamp to milliseconds
  } else if (timestamp.$numberLong) {
    date = new Date(parseInt(timestamp.$numberLong, 10) * 1000);
  } else {
    return "";
  }

  if (isNaN(date.getTime())) return "";

  const dateStr = new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);

  const timeStr = new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);

  return `${dateStr} kl. ${timeStr}`;
};

const formatCreditorExternalId = (
  id: string | undefined,
): string | undefined => {
  if (!id) return undefined;
  // Add dash before the last 4 digits (e.g., "1102982269" -> "110298-2269")
  if (id.length > 4) {
    return `${id.slice(0, -4)}-${id.slice(-4)}`;
  }
  return id;
};

const formatDebtorBban = (bban: string | undefined): string | undefined => {
  if (!bban) return undefined;
  // Format as "1234-26-123456" (4 digits, dash, 2 digits, dash, 6 digits)
  const cleaned = bban.replace(/\D/g, ""); // Remove non-digits
  if (cleaned.length === 12) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 6)}-${cleaned.slice(6)}`;
  }
  return bban; // Return as-is if not 12 digits
};

export default function Receipt({ payment }: ReceiptProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const amount = payment.amount || 0;
  const merchantName = payment.merchantName || payment.creditorName || "";
  // Check for creditorExternalId in multiple places (top-level, nested, and handle typo variant)
  const creditorExternalId = formatCreditorExternalId(
    payment.creditorExternalId,
  );
  const items = payment.items || [];
  const paymentDate = formatDate(payment.createdAt);

  // Get items/descriptions
  const getReceiptItems = (): string[] => {
    const receiptItems: string[] = [];

    // For Blink payments, add blinkName first if available
    if (payment.type === "blink" && payment.blinkName) {
      const blinkInItems = items.some(
        (item) => item.name === payment.blinkName,
      );
      if (!blinkInItems) {
        receiptItems.push(payment.blinkName);
      }
    }

    // Add items
    if (items.length > 0) {
      items.forEach((item) => {
        if (item.name) {
          receiptItems.push(item.name);
        }
      });
    }

    // Fallback to remittance information if no items
    if (receiptItems.length === 0 && payment.message) {
      receiptItems.push(payment.message);
    }

    return receiptItems;
  };

  const receiptItems = getReceiptItems();

  const handleDownloadPDF = async () => {
    if (!payment.id || isDownloading) return;

    setIsDownloading(true);
    try {
      const asPdf = pdf(<ReceiptPDF payment={payment} />);
      const blob = await asPdf.toBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kvittun-${payment.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Failed to download receipt:", error);
      // TODO: Show error toast/notification
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className="flex flex-col items-center w-full gap-6 px-4 pt-8 pb-12"
      style={{ fontFamily: '"DM Sans", sans-serif' }}
    >
      {/* Success indicator with Lottie animation */}
      <div className="flex flex-col items-center gap-4">
        <Lottie
          src={successAnimation}
          style={{
            width: isMobileOnly ? 160 : 200,
            height: isMobileOnly ? 160 : 200,
          }}
          autoplay
          loop={false}
        />
        <h2
          className="text-2xl font-semibold text-foreground"
          style={{ fontFamily: '"DM Sans", sans-serif' }}
        >
          {payment.source === "p2pApi"
            ? "Millifærsla tókst!"
            : "Greiðsla tókst!"}
        </h2>
      </div>

      {/* Receipt card */}
      <Card
        className="w-full max-w-md"
        style={{ fontFamily: '"DM Sans", sans-serif' }}
      >
        <CardContent className="pt-6 pb-6">
          {/* Creditor name at the top */}
          {merchantName && (
            <div className="text-sm text-muted-foreground pb-4 border-b border-border">
              <div>{merchantName}</div>
              {creditorExternalId && (
                <div className="text-xs mt-1">{creditorExternalId}</div>
              )}
            </div>
          )}

          {/* Description/Items */}
          {receiptItems.length > 0 && (
            <div className="space-y-1 pt-4 pb-4">
              {receiptItems.map((item, index) => (
                <div key={index} className="text-sm text-card-foreground">
                  {item}
                </div>
              ))}
            </div>
          )}

          {/* Quantity */}
          {payment.quantity !== undefined && payment.quantity !== null && (
            <div className="flex justify-between items-center pb-4">
              <span className="text-sm text-muted-foreground font-medium">
                Fjöldi
              </span>
              <span className="text-sm font-semibold text-card-foreground">
                {payment.quantity}
              </span>
            </div>
          )}

          {/* Amount and date at the bottom */}
          <div className="pt-4 border-t border-border space-y-2">
            {formatDebtorBban(payment.debtorBban) && (
              <div className="text-sm text-muted-foreground">
                {formatDebtorBban(payment.debtorBban)}
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground font-medium">
                Upphæð
              </span>
              <span className="text-lg font-semibold text-card-foreground">
                {formatCurrency(amount)}
              </span>
            </div>
            {paymentDate && (
              <div className="text-xs text-muted-foreground pt-1">
                Greitt {paymentDate}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action button with spacing for logo */}
      <div className="flex justify-center w-full max-w-md">
        <Button
          onClick={handleDownloadPDF}
          disabled={isDownloading}
          variant="ghost"
          size="sm"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" />
          {isDownloading ? "Sæki..." : "Sækja PDF"}
        </Button>
      </div>
    </div>
  );
}
